import { createTheme } from '@mui/material/styles';
import '@mui/x-date-pickers/themeAugmentation';

// Extend the MUI palette to include custom colors
declare module '@mui/material/styles' {
  interface Palette {
    neutral: Palette['primary'];
    badge: {
      new: string;
      used: string;
    };
  }
  interface PaletteOptions {
    neutral?: PaletteOptions['primary'];
    badge?: {
      new?: string;
      used?: string;
    };
  }
}

// ROCKEASE Seconde Vie brand colors based on design mockup
export const theme = createTheme({
  palette: {
    primary: {
      main: '#2C4965', // Dark navy blue for header and primary actions
      light: '#4A7C99',
      dark: '#1E4459',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#FFED00', // Bright yellow for CTAs and highlights
      light: '#FFF033',
      dark: '#CCB800',
      contrastText: '#1E293B',
    },
    error: {
      main: '#E7000B',
      light: '#FFE2E2',
      dark: '#B91C1C',
    },
    warning: {
      main: '#F59E0B',
      light: '#FBBF24',
      dark: '#D97706',
    },
    info: {
      main: '#3B82F6',
      light: '#60A5FA',
      dark: '#2563EB',
    },
    success: {
      main: '#10B981',
      light: '#34D399',
      dark: '#059669',
    },
    neutral: {
      main: '#64748B', // Medium gray for secondary text
      light: '#94A3B8',
      dark: '#475569',
      contrastText: '#FFFFFF',
    },
    badge: {
      new: '#FFE500', // Yellow badge for "Neuf"
      used: '#E5E7EB', // Light gray badge for "Occasion"
    },
    background: {
      default: '#F9FAFB', // Light gray background
      paper: '#FFFFFF', // White cards
    },
    text: {
      primary: '#1E293B', // Dark charcoal for primary text
      secondary: '#64748B', // Medium gray for secondary text
      disabled: '#94A3B8',
    },
    divider: '#E2E8F0',
  },
  typography: {
    fontFamily: [
      '"Montserrat"',
      '-apple-system',

      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
      color: '#1E293B',
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      color: '#1E293B',
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
      color: '#1E293B',
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      color: '#1E293B',
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      color: '#1E293B',
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      color: '#1E293B',
    },
    body1: {
      fontSize: '1rem',
      color: '#1E293B',
    },
    body2: {
      fontSize: '0.875rem',
      color: '#64748B',
    },
    caption: {
      fontSize: '0.75rem',
      color: '#64748B',
    },
  },
  shape: {
    borderRadius: 12, // Rounded corners for cards
  },
  components: {
    MuiTextField: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiPickersTextField: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiPickersInputBase: {
      styleOverrides: {
        root: {
          minHeight: 44,
        },
        input: {
          padding: '8px 12px',
          fontSize: '16px',
          '@media (min-width:600px)': {
            fontSize: '14px',
          },
        },
      },
    },
    MuiPickersOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: 'background.paper',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: 'background.paper',
          minHeight: 44,
        },
        input: ({ theme }) => ({
          padding: '8px 12px',
          fontSize: '16px',
          [theme.breakpoints.up('sm')]: {
            fontSize: '14px',
          },
        }),
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: ({ theme }) => ({
          padding: '8px 12px',
          minHeight: 'unset',
          display: 'flex',
          alignItems: 'center',
          fontSize: '16px',
          [theme.breakpoints.up('sm')]: {
            fontSize: '14px',
          },
        }),
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginTop: 4,
          marginLeft: 0,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 12,
          fontWeight: 600,
          padding: '10px 24px',
        },
        containedPrimary: {
          backgroundColor: '#2C5F7C',
          '&:hover': {
            backgroundColor: '#1E4459',
          },
        },
        containedSecondary: {
          backgroundColor: '#FFE500',
          color: '#1E293B',
          '&:hover': {
            backgroundColor: '#CCB800',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          transition: 'box-shadow 0.3s ease',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
          fontSize: '0.75rem',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#2C5F7C',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          backgroundColor: '#FFE500',
          color: '#1E293B',
          fontWeight: 600,
        },
      },
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          color: '#717182', // Inactive menu item color (light gray)
          '&.Mui-selected': {
            color: '#2C5F7C', // Active menu item color (primary navy)
          },
          '& .MuiSvgIcon-root': {
            fontSize: '1.5rem',
          },
        },
        label: {
          fontSize: '0.75rem',
          '&.Mui-selected': {
            fontSize: '0.75rem',
            fontWeight: 600,
          },
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(15, 23, 42, 0.55)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          '&.MuiBackdrop-invisible': {
            backgroundColor: 'transparent',
            backdropFilter: 'none',
            WebkitBackdropFilter: 'none',
          },
        },
      },
    },
  },
});
